import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Form, Formik } from 'formik';
import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

import { useLoader } from '../../provider/LoaderProvider';
import FormFields from './FormFields';
import { FormButtonPanelUI, FormHeaderUI } from '../Interface';
import useUsers from '../../context/UsersContext';
import dayjs from 'dayjs';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import useMenus from '../../context/MenuContext';
import AdHocTaskTabs from './AdHocTaskTabs';
import simpletaskService from '../../service/simpletask.service';

export const AdHocTaskPage = () => {
  const { getUser, user } = useUsers();
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const { showLoader, hideLoader } = useLoader();
  const [initValues, setInitValues] = useState({});
  const [submenu, setSubmenu] = useState(null);
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    project: [],
    user: [],
    type: [],
    status: [],
    company: [],
    priority: [],
  });
  const [autocompleteData, setAutocompleteData] = useState({});

  const def = {
    startDate: dayjs(),
    endDate: dayjs().add(1, 'hour'),
    projectId: null,
    simpleTaskName: '',
    duration: 1,
    resposiblePersonId: null,
    simpleTaskTypeId: null,
    statusId: null,
    companyId: null,
    simpleTaskDescription: '',
    simpleTaskAttachment: [],
    preparedness: '',
    priorId: null,
    simpleTaskRespPersonlist: [],
  };

  const defaultRespPerson = {
    id: null,
    isNew: true,
    userId: null,
    roleName: '',
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    setInitValues(def);
    getMenus();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('simpleTask');
    setSubmenu(submenuId);
  }, [menus]);

  useEffect(() => {
    if (submenu) {
      initDefaultValues();
    }
  }, [submenu]);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setInitValues((prev) => ({
        ...prev,
        simpleTaskTypeId: autocompleteData?.type?.defaultValue ?? null,
        statusId: autocompleteData?.status?.defaultValue ?? null,
        priorId: autocompleteData?.priority?.defaultValue ?? null,
      }));
    }
  }, [autocompleteData]);

  const validationSchema = Yup.object()
    .shape({
      projectId: Yup.string().nullable(),
      companyId: Yup.string().nullable(),
      startDate: Yup.string().required(),
      endDate: Yup.string().required(),
      simpleTaskName: Yup.string().required(),
      duration: Yup.number().required(),
      resposiblePersonId: Yup.string().nullable(),
      simpleTaskTypeId: Yup.string().required(),
      statusId: Yup.string().required(),
      priorId: Yup.string().required(),
      simpleTaskDescription: Yup.string().nullable().max(1500),
      preparedness: Yup.number().nullable(),
      simpleTaskRespPersonlist: Yup.array().of(
        Yup.object().shape({
          userId: Yup.string().required(),
          roleName: Yup.string().required(),
        })
      ),
    })
    .test('project-or-company-required', function (values) {
      const { projectId, companyId } = values || {};

      if (!projectId && !companyId) {
        return this.createError({
          path: 'projectId',
          message: 'Projekt vagy cég kitöltése kötelező!',
        });
      }

      return true;
    });

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .typeToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .priorityToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          user: resp[1],
          company: resp[2],
          type: resp[3],
          status: resp[4],
          priority: resp[5],
        };
        setAutocompleteData(aData);

        const filteredProject = aData.project.filter((p) => p.active);

        const acOptions = {
          project: generateAutocompleteItems(filteredProject, 'projectName', 'projectId'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          type: generateAutocompleteItems(aData.type.typeItems, 'itemName', 'itemId'),
          status: generateAutocompleteItems(aData.status.statusItems, 'itemName', 'itemId'),
          priority: generateAutocompleteItems(aData.priority.priorityListItems, 'itemName', 'itemId'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const handleInsertObject = (values, { resetForm }) => {
    showLoader();

    const foundStatus = autocompleteData.status.statusItems.find((status) => status.itemId === values.statusId);

    const insertObject = {
      ...values,
      userId: user.userId,
      createdBy: user.userId,
      duration: parseFloat(values.duration),
      preparedness: parseFloat(foundStatus?.preparedness ?? null),
      startDate: dayjs(values.startDate).toISOString(),
      endDate: dayjs(values.endDate).toISOString(),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.simpleTaskAttachment) {
      for (const file of insertObject.simpleTaskAttachment) {
        formData.append('files', file);
      }
    }

    simpletaskService
      .createSimpleTask(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        resetForm();
      })
      .finally(() => {
        hideLoader();
      });
  };

  return (
    <div className={`md:pt-20 p-2 fixed top-0 left-0 overflow-auto bg-gray-100 h-full w-full`}>
      <div className={`py-5 px-5 max-w-[650px]  mx-auto h-auto bg-white rounded-lg`}>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleInsertObject}
          enableReinitialize
        >
          {() => (
            <Form>
              <FormFields autocompleteOptions={autocompleteOptions} user={user} autocompleteData={autocompleteData} />
              <AdHocTaskTabs defaultField={defaultRespPerson} autocompleteOptions={autocompleteOptions} />

              <FormButtonPanelUI />
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
